/* eslint-disable no-param-reassign */
import { prefersReducedMotion } from './motion-utils'

/**
 * v-reveal — fade/pop an element in when it scrolls into view (the Play
 * reveal-on-scroll from the mockups). The directive adds the base
 * `play-reveal` class (hidden + offset) on bind and `is-in` when the element
 * crosses the viewport threshold; the pop animation lives in
 * src/styles/motion.scss.
 *
 * Usage:
 *   <section v-reveal>…</section>
 *   <section v-reveal="{ threshold: .25 }">…</section>
 *   <div class="stats" v-reveal="{ onReveal: startCounters }">…</div>
 *
 * Binding value (optional):
 *   threshold — IntersectionObserver threshold (default .14, from the mockup)
 *   once      — unobserve after first reveal (default true)
 *   onReveal  — callback(el) fired once the element reveals (e.g. to kick off
 *               count-ups, exactly like the mockup's stats band)
 *
 * When the user prefers reduced motion the element is shown immediately and
 * `onReveal` still fires, but the motion is skipped.
 *
 * VUE3-MIGRATION: rename `inserted`→`mounted`, `unbind`→`unmounted`. The
 * `IntersectionObserver` body is unchanged.
 */

const STORE = '__playReveal__'
const REVEAL_CLASS = 'play-reveal'
const IN_CLASS = 'is-in'

function optsFromBinding(binding) {
  const v = binding.value || {}
  return {
    threshold: typeof v.threshold === 'number' ? v.threshold : 0.14,
    once: v.once !== false,
    onReveal: typeof v.onReveal === 'function' ? v.onReveal : null,
  }
}

export const reveal = {
  inserted(el, binding) {
    const { threshold, once, onReveal } = optsFromBinding(binding)

    // Reduced motion: reveal instantly, still run the callback, no animation.
    if (prefersReducedMotion()) {
      if (onReveal) onReveal(el)
      return
    }

    el.classList.add(REVEAL_CLASS)

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        entry.target.classList.add(IN_CLASS)
        if (onReveal) onReveal(entry.target)
        if (once) io.unobserve(entry.target)
      })
    }, { threshold })

    io.observe(el)
    el[STORE] = io
  },

  unbind(el) {
    const io = el[STORE]
    if (!io) return
    io.disconnect()
    delete el[STORE]
  },
}

export default reveal
